// Fleet data layer (#59). Reads `GET /v1/runners` — the account's machine roster, which
// onboarding and the box-donation step read (the Fleet page's machines are
// `GET /v1/fleet/utilization`'s since #6560) — and `GET /v1/tasks` — the queue.
// Read-only, over `apiFetch` with the session bearer. Both routes speak the shared
// cursor grammar, so both hooks fetch ONE page at a cursor; the Fleet board's queue
// accumulates pages through `usePagedQuery` (`lib/paged.ts`) behind <LoadMore/>, live
// on the account stream's `tasks` topic ({@link useTaskQueue} says how a deeper window
// stays fresh).

import type {
  MoveItemState,
  OutcomeKind,
  SshTarget,
  TaskLifecycleBucket,
  TaskStatus,
} from '@developerz/domain';
import {
  type RunnerHealth,
  type RunnerRevocationBody,
  RunnerRevocationWireSchema,
  type RunnerRosterWire,
  RunnerRosterWireSchema,
  renderBoxJoinCommand,
  TERMINAL_TASK_STATUSES,
} from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch, withCursor } from './api';
import { type CodingModel, parseCodingModel } from './coding-model';
import { useLiveChanges } from './live/live-changes';
import { type Page, type PagedList, usePagedQuery } from './paged';
import { pageSchema } from './paged-schema';
import { SESSION_TOKEN } from './session';

/**
 * The fine-grained diagnosis, as the WIRE publishes it (`RunnerHealthSchema`,
 * `@developerz/domain`). It used to be restated here, under a docblock saying
 * the value could not be imported because `boxHealth` lives in
 * `@developerz/mcp` — a comment asserting a guarantee the code no longer
 * provides. The resource layer (plan 101 doc 05) publishes the enum precisely so
 * the producer and every reader name one vocabulary, and `domain` IS a dashboard
 * dependency; `boxHealth`'s return type is checked against it, so a sixth state
 * cannot appear here without appearing there.
 */
export type BoxHealth = RunnerHealth;

/**
 * The masked runner projection `GET /v1/runners` returns — THE PUBLISHED
 * RESOURCE, not a copy of it. `RunnerRosterWireSchema` is the same schema
 * `apps/api` serializes the roster through (`RunnerRosterResource`), so there is
 * no second declaration left to drift.
 *
 * That is the close of the defect this file's half of plan 101 doc 15 was
 * written for. The mirror it began as had drifted from the server's projection
 * in five places and nothing in the repo could see it, because `apiFetch` ended
 * in a cast: `specs`, `ssh_host` and `stats` were projected on every roster row
 * and declared nowhere here, `display_name` was declared nullable, and `health`
 * optional. `lib/mocks.ts` was typed against THAT type, so the fixture agreed
 * with the wrong side and the Playwright lane was green over it too.
 *
 * The first fix replaced the hand-written type with a local `z.object` — correct
 * about every field, and still a SECOND declaration of a shape the wire package
 * already published, restating `BoxSpecsSchema`/`BoxStatsSchema` and the health
 * enum to do it. `packages/domain/src/wire/runner.ts` said so in its own header:
 * "the dashboard adoption is a separate change and until it lands the mirror is
 * still a mirror". This is that change.
 *
 * TWO VARIANTS, and the dashboard reads both. A roster row carries the cross-org
 * label and the presence fold; the single-box body a revoke answers carries
 * neither, which is why {@link revokeRunner} parses through
 * `RunnerRevocationWireSchema` (that single-box body, or `{ id, status }`) and
 * this one does not.
 *
 * Non-strict, as the wire package defines it: a `/v1` route gaining a field is
 * an ordinary additive change and must never break a bundle already in a
 * browser, so unknown keys are ignored. Only a field that VANISHED, changed type
 * or went null throws — see `parseBody` in `lib/api.ts`.
 */
/** One masked roster row. `Readonly` because this app treats a response as immutable. */
export type RunnerRow = Readonly<RunnerRosterWire>;

/**
 * What a revoke answers: the masked single-box body, or only `{ id, status:
 * 'revoked' }` when the revoked row no longer parses or the revoke is a retry.
 * `id` and `status` are on both arms; narrow on `'health' in body` before
 * reading anything else.
 */
export type RunnerBody = Readonly<RunnerRevocationBody>;

/**
 * The `GET /v1/runners` envelope, so the page and its rows are checked together.
 * Built by `pageSchema` (`lib/paged-schema.ts`) rather than restated: this was
 * the first parsed collection and its local `z.object({data, next_cursor})` was
 * about to become the shape every other one copied.
 */
const runnersPageSchema = pageSchema(RunnerRosterWireSchema);

export const RUNNERS_QUERY_KEY = 'fleet_runners';

/**
 * The Fleet watch cadence (#298): a roster read's default (the box-donation step's; the
 * Fleet page's machines are `GET /v1/fleet/utilization`'s since #6560) and the queue
 * board's `usePagedQuery` poll.
 */
export const FLEET_POLL_MS = 30_000;

/**
 * One page of the caller's runners, freshest-enrolled first. Idle with no
 * session. Revoked boxes are dropped — revoke IS the delete action (the BYOK-key
 * precedent, #892/#894): a killed box leaves the roster instead of lingering as
 * a dead card, and stays soft-revoked server-side so the audit trail keeps it.
 * The server applies that filter before paging ({@link fetchRunnersPage}).
 */
export function useRunners(
  cursor: () => string | null,
  token: string | null = SESSION_TOKEN,
  /**
   * Poll cadence. 30s is the watch surface's (#298); the onboarding box step
   * passes the funnel's own 5s so "your servers" flips to ready in the same
   * window the state poll that moves the card does — a list still saying
   * "starting up" under a card that already advanced would name two truths.
   */
  pollMs: number | false = FLEET_POLL_MS,
) {
  return createQuery<Page<RunnerRow>>(() => ({
    queryKey: [RUNNERS_QUERY_KEY, cursor(), token, activeAccountId()],
    enabled: token !== null,
    // Watch surface (#298): a box dropping offline shows without a reload.
    // Foreground-only poll (TanStack default), on the page currently keyed.
    refetchInterval: pollMs,
    queryFn: () => fetchRunnersPage(cursor(), token),
  }));
}

/**
 * The roster minus revoked boxes, filtered by the SERVER before it cuts the page
 * (api `RUNNER_LIST_SPEC` `filterable: ['status']`). Filtering on the client ran
 * within the slice already cut, so a page came back short — or empty with a
 * `next_cursor` still pointing past it.
 */
const RUNNERS_LIVE_PATH = '/v1/runners?filter=status:ne:revoked';

/** One roster page — {@link useRunners}'s read, exported for its test. */
export async function fetchRunnersPage(
  cursor: string | null,
  token: string | null,
): Promise<Page<RunnerRow>> {
  // The FIRST endpoint on the `{ schema }` overload (plan 101 doc 15 step 3),
  // because it is the one whose divergence was measured. A drifted roster
  // now surfaces as this panel's own <ErrorState/> naming the field, instead
  // of rendering a grid of blank cards nobody can diagnose from a screenshot.
  return apiFetch(withCursor(RUNNERS_LIVE_PATH, cursor), token, undefined, {
    schema: runnersPageSchema,
  });
}

/**
 * One queue row from `GET /v1/tasks` (`projectTask`, api `routes/tasks.ts`) —
 * repo + title off the stored envelope, `run_id` to the run's live timeline.
 * The same shape rides the SSE `tasks` topic (#513): `lifecycle_bucket` is
 * always present (the board's running-vs-queued split), `queue_position` is
 * the 1-based rank among the account's pending tasks (null once running),
 * `depth` the account's total pending backlog.
 */
export type TaskRow = {
  readonly id: string;
  readonly repo_full_name: string;
  readonly title: string;
  readonly status: TaskStatus;
  /** Coarse bucket the board lanes on — derived server-side from `status`. */
  readonly lifecycle_bucket: TaskLifecycleBucket;
  /** 1-based rank in the pending queue; null for any non-pending task. */
  readonly queue_position: number | null;
  /** The account's total pending backlog; null where not computed (detail). */
  readonly depth: number | null;
  /** What the task delivers — varies the `completed` badge (`pr` opens one, `report` doesn't). */
  readonly outcome_kind: OutcomeKind;
  readonly runner_id: string | null;
  readonly run_id: string | null;
  /** The epic (plan) this task belongs to (#511 task 4) — deep-links to `/fleet/plans/:plan_id`; null for a non-plan task. */
  readonly plan_id: string | null;
  /**
   * The machine this task was SENT to (#6767, `TaskResource`): only it may claim the task
   * until `target_until`, then it returns to the open queue; null when nobody sent it or
   * once released. Nullish on read (#6770): a bundle rolled ahead of its api reads it absent.
   */
  readonly target_runner_id?: string | null;
  /** When the send's hard target ends (send time + 10 min), ISO-8601; null with no target. */
  readonly target_until?: string | null;
  /**
   * The task's newest move between machines (#6768, the task resource's `move`): its batch,
   * the two machines, the item's state and when it may move again (`rest_until`, ISO). Null
   * when it never moved; nullish on read, as on the wire, so a bundle ahead of its api reads
   * it absent (#6772: the Moving and Moved tags, the resting rings and Undo read it).
   */
  readonly move?: {
    readonly batch_id: string;
    readonly from_runner_id: string | null;
    readonly to_runner_id: string | null;
    readonly state: MoveItemState;
    readonly rest_until: string;
  } | null;
  readonly created_at: string;
};

export const TASKS_QUERY_KEY = 'fleet_tasks';

/** The account's whole queue. `/fleet/work?machine=` reads one machine's (`tasksPath`). */
export const TASKS_PATH = '/v1/tasks';

/**
 * The Fleet queue board: every loaded page of `GET /v1/tasks` (or one machine's
 * narrow, `path`), LIVE on the account stream's `tasks` topic (#7612). Idle with
 * no session. Nothing here paints a stream row: the board is always the server's
 * pages, so it pages honestly behind Load more and never claims a capped head.
 *
 * A CHANGE RE-READS BY DEPTH (`useLiveChanges`, the paged half of the live layer).
 * On page one — the board as it opens, and the /fleet summary's head — it is
 * `reload()`, which re-reads page one in place. Past page one `reload()` would
 * `reset()` the accumulation, and on a busy fleet (a task moves every few seconds)
 * that throws the reader back to page one before they finish reading page two; so
 * there the window re-walk (`poll`, every loaded page from page one by keyset)
 * refreshes the whole window at the watch cadence instead. The walk also runs on
 * page one whenever the stream does not vouch.
 */
export function useTaskQueue(token: string | null, path: string = TASKS_PATH): PagedList<TaskRow> {
  let deep = (): boolean => false;
  // When page one's read now on screen started — a `ready` re-reads only a board
  // read before its gap began (`live-changes.ts`). Page one is what `reload()`
  // re-reads; past it the window walk keeps the deeper pages fresh.
  let pageOneAt = 0;
  const readPage = (cursor: string | null): Promise<Page<TaskRow>> => {
    if (cursor === null) pageOneAt = Date.now();
    return fetchTasksPage(cursor, token, path);
  };
  // Fires at least `LIVE_COALESCE_MS` after a signal, so `list` is always assigned by then.
  const live = useLiveChanges(
    () => [{ entity: 'task' }],
    token,
    () => {
      if (!deep()) list.reload();
    },
    () => pageOneAt,
  );
  const list: PagedList<TaskRow> = usePagedQuery<TaskRow>(
    (cursor) => {
      deep = () => cursor() !== null;
      return createQuery<Page<TaskRow>>(() => ({
        queryKey: [TASKS_QUERY_KEY, path, cursor(), token, activeAccountId()],
        enabled: token !== null,
        queryFn: () => readPage(cursor()),
      }));
    },
    {
      poll: {
        every: () => (deep() || !live.isLive() ? FLEET_POLL_MS : false),
        read: readPage,
      },
    },
  );
  return list;
}

/** One queue page — {@link useTaskQueue}'s read and window walk, and the machine panel's (`machine-panel-reads.ts`). */
export function fetchTasksPage(
  cursor: string | null,
  token: string | null,
  path: string = TASKS_PATH,
): Promise<Page<TaskRow>> {
  return apiFetch<Page<TaskRow>>(withCursor(path, cursor), token);
}

/** Newest-first, the `GET /v1/tasks` order — the board's row order. */
export function byTaskRecency(a: TaskRow, b: TaskRow): number {
  return b.created_at.localeCompare(a.created_at) || b.id.localeCompare(a.id);
}

/** Running-vs-queued counts for the board's band — derived from the rows on screen. */
export type QueueBoardSummary = {
  readonly running: number;
  readonly queued: number;
  /** Server's full pending backlog depth (identical on every row); null on pre-#513 rows. */
  readonly depth: number | null;
};

/**
 * Roll the board's rows up into the band's two counts by `lifecycle_bucket`
 * (#513). Pure so it's unit-tested without a query. Terminal buckets
 * (`succeeded`/`failed`) count in neither lane — the band is about the work
 * in motion, and the rows below carry their own status pills.
 */
export function summarizeQueue(tasks: readonly TaskRow[]): QueueBoardSummary {
  const count = (b: TaskLifecycleBucket): number =>
    tasks.filter((t) => t.lifecycle_bucket === b).length;
  // `depth` is the server's full pending total (identical on every row); prefer it
  // over the visible-row `queued` count so the band stays accurate above the
  // STREAM_SNAPSHOT_LIMIT cap, where the on-screen rows are only the queue head.
  const depth = tasks.find((t) => t.depth != null)?.depth ?? null;
  return { running: count('running'), queued: count('queued'), depth };
}

// ─── Connect a machine (#619, #6567) ─────────────────────────────────────────

/** `POST /v1/runners/enrollments` response — the one-time token, shown once. */
export type EnrollmentMint = {
  readonly enrollment_token: string;
  readonly runner_id: string;
  /**
   * When the code stops working, as the store wrote it (#6567). Optional only
   * because an api older than the field answers without it: the reveal then
   * states no expiry rather than a guessed one.
   */
  readonly expires_iso?: string;
  /**
   * The coding model the join command uses and where it came from (plan 102 slice
   * 05, `lib/coding-model.ts`); `null` when the answer carries none this build reads.
   */
  readonly coding_model: CodingModel | null;
};

/**
 * Mint a one-time box enrollment token. A 402 means the ai-dev seat cap is
 * reached (the problem detail carries the message). Never persisted client-side
 * — the caller renders it once and drops it.
 *
 * `model` (plan 102 slice 05) names the box's coding model; omitted, no body is
 * sent and the server prefills it from the account's verified keys.
 */
export async function mintEnrollment(
  token: string | null = SESSION_TOKEN,
  model?: string,
): Promise<EnrollmentMint> {
  const init: RequestInit =
    model === undefined ? { method: 'POST' } : { method: 'POST', body: JSON.stringify({ model }) };
  const raw = await apiFetch<Omit<EnrollmentMint, 'coding_model'> & { coding_model?: unknown }>(
    '/v1/runners/enrollments',
    token,
    init,
  );
  return { ...raw, coding_model: parseCodingModel(raw.coding_model) };
}

/**
 * `POST /v1/runners/enrollments/ssh` 202 body (#886). Note what it does NOT
 * carry: a token. That is the whole point of the transport — the credential
 * goes straight to the bootstrap lane and the operator never holds one — so
 * there is nothing to reveal on this path.
 *
 * `queued` is not enrolled: the mothership dials the box out of band and the
 * outcome lands as a `runner.ssh_bootstrap_{succeeded,failed}` audit row.
 */
export type SshEnrollQueued = {
  readonly runner_id: string;
  readonly status: 'queued';
  /** The coding model the bootstrap exports (plan 102 slice 05, `lib/coding-model.ts`). */
  readonly coding_model: CodingModel | null;
};

/**
 * `POST /v1/runners/enrollments/ssh` body — api `SshEnrollBodySchema`
 * (`routes/ssh-enroll-body.ts`): the box coordinates plus the coding `model`
 * the worker exports to the join as `DZ_MODEL_OVERRIDE`. Over SSH the operator
 * has no shell on the box to export it in, and the join refuses a fresh box
 * that names none. Optional on the wire (a re-join keeps the box's own); the
 * server's charset check is the arbiter.
 */
export type SshEnrollRequest = SshTarget & { readonly model?: string };

/**
 * Ask the platform to dial a box and run the join on it (#886/#1035). The body
 * is the server's own {@link SshEnrollRequest} shape; `vetSshTarget` (the SSRF +
 * argv-injection gate) stays the arbiter and answers 400 whose `detail` IS the
 * refusal reason, 402 on the shared ai-dev seat cap, and 503 either when the
 * deployment has no platform SSH key sealed (nothing minted) or when the
 * bootstrap enqueue failed AFTER the mint — that second 503 releases the seat it
 * had just reserved before answering (#1101), so both are retryable. Nothing
 * about that gate is re-implemented client-side.
 */
export async function sshEnroll(
  target: SshEnrollRequest,
  token: string | null = SESSION_TOKEN,
): Promise<SshEnrollQueued> {
  const raw = await apiFetch<Omit<SshEnrollQueued, 'coding_model'> & { coding_model?: unknown }>(
    '/v1/runners/enrollments/ssh',
    token,
    { method: 'POST', body: JSON.stringify(target) },
  );
  return { ...raw, coding_model: parseCodingModel(raw.coding_model) };
}

/**
 * Revoke a box credential. Returns {@link RunnerBody} — the SINGLE-BOX body
 * (the roster row minus its presence and cross-org blocks, never promising an
 * `org_login` it does not carry), or the reduced `{ id, status: 'revoked' }` the
 * route sends for a box whose row no longer parses and for a retry. Both are a
 * revoke that landed; a strict reduced arm still refuses a body that is neither.
 */
export function revokeRunner(
  runnerId: string,
  token: string | null = SESSION_TOKEN,
): Promise<RunnerBody> {
  return apiFetch(
    `/v1/runners/${encodeURIComponent(runnerId)}/revoke`,
    token,
    { method: 'POST' },
    { schema: RunnerRevocationWireSchema },
  );
}

/**
 * One task's stop verdict, as `POST /v1/tasks/:id/cancel` answers it (#1898).
 *
 * `already_terminal` is a 200, not an error: a stop pressed twice, or pressed on
 * work that landed a second earlier, is a request nothing was wrong with. The
 * caller tells the two apart by `kind`, never by the HTTP status.
 */
export type TaskCancelResult = {
  readonly task_id: string;
  readonly status: TaskStatus;
  readonly kind: 'cancelled' | 'already_terminal';
  readonly previous_runner_id: string | null;
};

/**
 * May this task be stopped? Mirrors the SERVER's own predicate — the UPDATE in
 * `cancelTask` (`@developerz/db`) refuses exactly `TERMINAL_TASK_STATUSES` — so
 * the control is offered where it works and absent where it does not (#1906).
 *
 * READ THE STATUS, NOT `lifecycle_bucket`. The bucket files `completed` under
 * `succeeded`, which reads like "done, nothing to stop" and is wrong: a
 * `completed` task has landed a PR and is still under the babysit watch, so the
 * server cancels it happily. Gating on the bucket would hide the control on the
 * exact rows an operator most wants it on.
 *
 * The one case this CANNOT see is a `pr_review` task: the carve-out lives in the
 * task's envelope, which the row does not carry. Those answer 409 with a reason,
 * which the caller surfaces — offering a control that sometimes refuses out loud
 * beats hiding it from every row on a field we do not have.
 */
export function canStopTask(status: TaskStatus): boolean {
  return !TERMINAL_TASK_STATUSES.includes(status as (typeof TERMINAL_TASK_STATUSES)[number]);
}

/** Stop one task. 404 ⇒ unknown/foreign; 409 ⇒ a `pr_review` task the carve-out refuses. */
export function cancelTask(
  taskId: string,
  token: string | null = SESSION_TOKEN,
): Promise<TaskCancelResult> {
  return apiFetch<TaskCancelResult>(`/v1/tasks/${encodeURIComponent(taskId)}/cancel`, token, {
    method: 'POST',
  });
}

/**
 * The copy-paste join one-liner for enrolling a box — shell-history safe (06b):
 * `renderBoxJoinCommand` (`@developerz/domain`), the ONE spelling MCP's
 * `join_command` renders too; its docblock says why it asks for the model first,
 * reads the token into a header and downloads before it runs. The operator pastes
 * the minted token at the `Enrollment token:` prompt; the modal reveals it
 * separately. `apiBase` is trailing-slash normalized (matching the SSH bootstrap +
 * `buildJoinScript`) so a `PUBLIC_API_URL` ending in `/` can't yield `//join`.
 */
export function joinOneLiner(apiBase: string): string {
  return renderBoxJoinCommand(`${apiBase.replace(/\/+$/, '')}/join`);
}
